import { useState, useEffect, useCallback, useRef } from 'react';
import V2ApiClient from '../services/V2ApiClient';

/**
 * Main V2 Workflow hook - handles all workflow execution logic
 * Clean implementation focused only on V2 workflows
 */
export const useV2Workflow = (workflowId, token, initialRunId = null) => {
  // Core state
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [runId, setRunId] = useState(initialRunId);
  const [executionStatus, setExecutionStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [lastEventTimestamp, setLastEventTimestamp] = useState(null);

  // Simple progressive display - just track pending messages to show with delay
  const [pendingMessages, setPendingMessages] = useState([]);

  // Services
  const apiClientRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastEventTimestampRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const runIdRef = useRef(runId);  // 🔧 FIX: Track current runId with ref to avoid stale closure
  const abortControllerRef = useRef(null);  // 🔧 FIX: Cancel in-flight requests on cleanup

  // Keep runIdRef in sync with state
  useEffect(() => {
    runIdRef.current = runId;
  }, [runId]);

  // Helper function to process messages with timing delay
  const processMessagesWithTiming = useCallback((newEvents, skipDelay = false) => {
    console.log('🚀🚀🚀 VERSION 13.0 - FULL DEBUG LOGGING ENABLED 🚀🚀🚀');
    console.log('🔥🔥🔥 IF YOU DONT SEE THIS MESSAGE THE BROWSER IS USING CACHED JS FILES 🔥🔥🔥');
    console.log('📨 PROCESSING EVENTS - Raw newEvents:', newEvents);
    console.log('📨 PROCESSING EVENTS - newEvents length:', newEvents.length);
    console.log('📨 PROCESSING EVENTS - skipDelay:', skipDelay);

    const chatMessages = newEvents
      .filter(update => update.type === 'chat_message' &&
             ['assistant', 'tool_call', 'tool_result', 'workflow_step'].includes(update.data.message_type))
      .map(update => {
        // Check if this is a workflow_step with component_definition (UI component)
        let isUIComponent = false;
        let parsedContent = update.data.content;

        if (update.data.message_type === 'workflow_step') {
          try {
            if (typeof update.data.content === 'string') {
              parsedContent = JSON.parse(update.data.content);
            }
            if (parsedContent && parsedContent.status === 'ui_ready' && parsedContent.component_definition) {
              isUIComponent = true;
              console.log('🎯 Found UI component in workflow_step message:', parsedContent.component_definition?.metadata?.name);
            }
          } catch (e) {
            // Not JSON, continue
          }
        }

        return {
          id: update.data.message_id,
          type: update.data.message_type,
          content: isUIComponent ? parsedContent : update.data.content,
          timestamp: update.data.timestamp,
          step_name: update.data.step_name,
          tool_name: update.data.tool_name,
          isUIComponent
        };
      });

    console.log('💬 CHAT MESSAGES EXTRACTED:', chatMessages.length, 'messages');
    chatMessages.forEach((msg, idx) => {
      console.log(`💬 Chat Message ${idx}:`, {
        id: msg.id,
        type: msg.type,
        step: msg.step_name,
        timestamp: msg.timestamp,
        contentPreview: typeof msg.content === 'string' ? msg.content.substring(0, 100) : 'object'
      });
    });

    // Also process workflow events that contain UI component definitions
    console.log('🔍 LOOKING FOR WORKFLOW EVENTS WITH UI COMPONENTS...');

    const workflowEvents = newEvents.filter(update => update.type === 'workflow_event');
    console.log('🏗️ WORKFLOW EVENTS FOUND:', workflowEvents.length);

    const completedEvents = workflowEvents.filter(update => update.data.event_type === 'step.completed');
    console.log('✅ COMPLETED EVENTS FOUND:', completedEvents.length);

    const eventsWithStructuredOutput = completedEvents.filter(update => update.data.data?.structured_output);
    console.log('📊 EVENTS WITH STRUCTURED OUTPUT:', eventsWithStructuredOutput.length);

    const eventsWithComponentDef = eventsWithStructuredOutput.filter(update => update.data.data?.structured_output?.component_definition);
    console.log('🎯 EVENTS WITH COMPONENT DEFINITION:', eventsWithComponentDef.length);

    // Log each workflow event in detail
    workflowEvents.forEach((event, index) => {
      console.log(`🔍 WORKFLOW EVENT ${index}:`, {
        type: event.type,
        event_type: event.data?.event_type,
        has_data: !!event.data,
        has_nested_data: !!event.data?.data,
        has_structured_output: !!event.data?.data?.structured_output,
        structured_output_keys: event.data?.data?.structured_output ? Object.keys(event.data.data.structured_output) : [],
        has_component_def: !!event.data?.data?.structured_output?.component_definition,
        component_name: event.data?.data?.structured_output?.component_definition?.metadata?.name
      });

      if (event.data?.data?.structured_output?.component_definition) {
        console.log(`🎯 COMPONENT DEFINITION FOUND IN EVENT ${index}:`, event.data.data.structured_output.component_definition);
      }
    });

    const uiComponentEvents = newEvents
      .filter(update => update.type === 'workflow_event' &&
             update.data.event_type === 'step.completed' &&
             update.data.data?.structured_output &&
             update.data.data.structured_output.component_definition)
      .map(update => {
        const componentDef = update.data.data.structured_output.component_definition;
        console.log('🎯 UI Component Event found:', componentDef);
        console.log('🎯 Creating UI message with content:', update.data.data.structured_output);
        // Use step_name + timestamp for stable ID instead of Date.now()
        const stableId = `ui_${update.data.data.step_name}_${update.data.timestamp}`;
        return {
          id: stableId,
          type: 'assistant',
          content: update.data.data.structured_output,
          timestamp: update.data.timestamp,
          step_name: update.data.data.step_name,
          isUIComponent: true
        };
      });

    console.log('🎨 UI COMPONENT EVENTS CREATED:', uiComponentEvents.length);
    uiComponentEvents.forEach((event, index) => {
      console.log(`🎨 UI EVENT ${index}:`, event);
    });

    // Combine and sort all messages
    const allMessages = [...chatMessages, ...uiComponentEvents]
      .sort((a, b) => {
        const timeA = new Date(a.timestamp);
        const timeB = new Date(b.timestamp);

        // If timestamps are very close (within 2 seconds), prioritize tool_call before tool_result
        if (Math.abs(timeA - timeB) < 2000) {
          if (a.type === 'tool_call' && b.type === 'tool_result') return -1;
          if (a.type === 'tool_result' && b.type === 'tool_call') return 1;
        }

        return timeA - timeB;
      });

    if (allMessages.length === 0) return;

    // Group messages by step for timing
    const messagesByStep = {};
    allMessages.forEach(msg => {
      const key = msg.step_name || 'general';
      if (!messagesByStep[key]) messagesByStep[key] = [];
      messagesByStep[key].push(msg);
    });

    // Add messages with progressive timing (or immediately if skipDelay is true)
    if (skipDelay) {
      // Add new messages and sort by timestamp to ensure proper order
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id)); // Use Set for O(1) lookup
        const newMessages = allMessages.filter(msg => !existingIds.has(msg.id));

        console.log('⚡ SKIP_DELAY: Adding', newMessages.length, 'messages immediately');
        console.log('⚡ SKIP_DELAY: Previous message count:', prev.length);

        // Combine and sort all messages by timestamp
        const combined = [...prev, ...newMessages].sort((a, b) => {
          const timeA = new Date(a.timestamp);
          const timeB = new Date(b.timestamp);

          // If timestamps are very close (within 2 seconds), prioritize tool_call before tool_result
          if (Math.abs(timeA - timeB) < 2000) {
            if (a.type === 'tool_call' && b.type === 'tool_result') return -1;
            if (a.type === 'tool_result' && b.type === 'tool_call') return 1;
          }

          return timeA - timeB;
        });

        return combined;
      });
    } else {
      // Progressive display with timing - show messages sequentially for real-time feel
      // But UI components (modals) should show immediately
      const uiMessages = allMessages.filter(msg => msg.isUIComponent);
      const regularMessages = allMessages.filter(msg => !msg.isUIComponent);

      // Show UI component messages immediately (no delay for modals)
      if (uiMessages.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newUiMessages = uiMessages.filter(msg => !existingIds.has(msg.id));
          if (newUiMessages.length > 0) {
            const combined = [...prev, ...newUiMessages].sort((a, b) => {
              return new Date(a.timestamp) - new Date(b.timestamp);
            });
            return combined;
          }
          return prev;
        });
      }

      // Show regular messages with progressive delay
      // Use 50ms delay (reduced from 200ms) and cap at 2 seconds max
      regularMessages.forEach((msg, msgIndex) => {
        const delay = Math.min(msgIndex * 50, 2000); // 50ms between each, max 2 seconds

        setTimeout(() => {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            if (!existingIds.has(msg.id)) {
              const combined = [...prev, msg].sort((a, b) => {
                const timeA = new Date(a.timestamp);
                const timeB = new Date(b.timestamp);
                if (Math.abs(timeA - timeB) < 2000) {
                  if (a.type === 'tool_call' && b.type === 'tool_result') return -1;
                  if (a.type === 'tool_result' && b.type === 'tool_call') return 1;
                }
                return timeA - timeB;
              });
              return combined;
            }
            return prev;
          });
        }, delay);
      });
    }
  }, []);

  // Polling mechanism to replace websockets
  const startPollingForResults = useCallback((currentRunId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log('🔄 Hook: Starting polling for run', currentRunId);
    pollingStartTimeRef.current = Date.now();

    pollingIntervalRef.current = setInterval(async () => {
      const pollTime = new Date().toISOString();
      console.log(`🔄 POLLING TICK at ${pollTime} - Checking for updates...`);

      // Add timeout protection - stop polling after 5 minutes
      const pollingDuration = Date.now() - pollingStartTimeRef.current;
      if (pollingDuration > 300000) { // 5 minutes
        console.log('⚠️ Hook: Polling timeout reached (5 minutes), stopping polling');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsLoading(false);
        setError('Workflow polling timeout - please refresh the page');
        return;
      }
      try {
        console.log(`📡 FETCHING STATUS for run_id: ${currentRunId}`);
        const status = await apiClientRef.current.getWorkflowStatus(currentRunId);

        if (status && status.success && status.data) {
          console.log('📊 Hook: Polling status:', status);
          console.log('📊 Hook: Workflow status:', status.data.workflow_run?.status);
          console.log('📊 Hook: Current step:', status.data.workflow_run?.current_step);

          const workflowRun = status.data.workflow_run;
          const currentStatus = workflowRun ? workflowRun.status : null;
          const stepName = workflowRun ? workflowRun.current_step : null;

          // Update execution status and current step
          if (currentStatus) {
            setExecutionStatus(currentStatus);
            console.log('🔄 Hook: Updated status to:', currentStatus);
          }
          if (stepName) {
            setCurrentStep(stepName);
            console.log('🔄 Hook: Updated current step to:', stepName);
          }

          // Fetch workflow events to get chat messages (only new ones since last timestamp)
          try {
            console.log(`📡 FETCHING EVENTS for run_id: ${currentRunId}, since: ${lastEventTimestampRef.current}`);
            const events = await apiClientRef.current.getWorkflowEvents(currentRunId, lastEventTimestampRef.current);
            console.log(`📡 EVENTS RESPONSE:`, {
              success: events?.success,
              updateCount: events?.updates?.length || 0,
              updates: events?.updates
            });
            if (events && events.success && events.updates) {
              console.log('📨 Hook: Processing workflow events:', events.updates.length, 'new events since', lastEventTimestampRef.current);
              console.log('📨 Hook: Event details:', events.updates.map(u => ({
                type: u.type,
                timestamp: u.timestamp,
                messageType: u.data?.message_type,
                step: u.data?.step_name,
                contentPreview: typeof u.data?.content === 'string' ? u.data.content.substring(0, 60) : 'object'
              })));

              // DEBUG: Log all events to see structure
              events.updates.forEach((update, index) => {
                console.log(`🔍 Hook DEBUG Event ${index}:`, {
                  type: update.type,
                  event_type: update.data?.event_type,
                  has_structured_output: !!update.data?.structured_output,
                  has_component_def: !!update.data?.structured_output?.component_definition,
                  structured_output_keys: update.data?.structured_output ? Object.keys(update.data.structured_output) : []
                });
              });

              // Process new messages with slight delay for better UX
              // This shows messages one-by-one as they arrive instead of all at once
              processMessagesWithTiming(events.updates, false); // skipDelay=false for progressive display

              // Update last event timestamp to the latest event's timestamp
              if (events.updates.length > 0) {
                const latestTimestamp = events.updates[events.updates.length - 1].timestamp;
                lastEventTimestampRef.current = latestTimestamp;
                setLastEventTimestamp(latestTimestamp);
                console.log('⏰ Hook: Updated last event timestamp to:', latestTimestamp);
              }
            }
          } catch (eventsError) {
            // Ignore abort errors - they're expected when navigating away
            if (eventsError.name === 'AbortError') {
              console.log('🛑 Hook: Events fetch aborted (user navigated away)');
              return;
            }
            console.error('❌ Hook: Error fetching events:', eventsError);
            // Don't stop polling on events error, continue with status polling
          }

          // Stop polling if workflow is in a terminal state (but keep polling for awaiting_user to detect UI steps)
          if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'cancelled' || currentStatus === 'paused') {
            console.log('🏁🏁🏁 WORKFLOW REACHED TERMINAL STATE 🏁🏁🏁');
            console.log('✅ Hook: Workflow reached stable state, doing final message fetch before stopping polling. Status:', currentStatus);
            console.log('🔍 Hook: Last event timestamp before final fetch:', lastEventTimestampRef.current);
            console.log('🔍 Hook: Current time:', new Date().toISOString());

            // UX FIX: Hide loading spinner IMMEDIATELY when workflow completes
            // This removes the "Executing workflow..." spinner right away for better UX
            // Final fetches will continue in background to catch any remaining messages
            console.log('✅ Workflow completed - hiding loading spinner immediately');
            setIsLoading(false);

            // CRITICAL: Stop regular polling IMMEDIATELY to avoid duplicate fetches
            // Final fetches will run independently using setTimeout
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
              console.log('🛑 Stopped regular polling interval - final fetches will run independently');
            }

            // Capture runId to prevent cross-workflow contamination
            const completedRunId = currentRunId;

            // Do MULTIPLE final fetches to catch any messages created during workflow completion
            // Fetch at 500ms, 1000ms, 1500ms, 2000ms, 2500ms, and 3000ms to ensure we catch all messages
            let fetchCount = 0;
            const maxFetches = 6; // Increased from 3 to 6
            const fetchInterval = 500;

            const doFinalFetch = async () => {
              // Safety check: Stop if runId changed (new workflow started)
              // 🔧 FIX: Use runIdRef.current instead of runId to avoid stale closure
              if (completedRunId !== runIdRef.current) {
                console.log('🛑 Final fetch stopped - runId changed (new workflow started)');
                return;
              }

              fetchCount++;
              try {
                console.log(`🔄 Hook: Final fetch ${fetchCount}/${maxFetches}, since: ${lastEventTimestampRef.current}`);

                // Use timestamp-based filtering to only fetch NEW messages
                const finalEvents = await apiClientRef.current.getWorkflowEvents(currentRunId, lastEventTimestampRef.current);

                if (finalEvents && finalEvents.success && finalEvents.updates && finalEvents.updates.length > 0) {
                  console.log(`🔄 Hook: Final fetch ${fetchCount} found ${finalEvents.updates.length} new messages`);

                  // Show final messages immediately without delay
                  processMessagesWithTiming(finalEvents.updates, true);

                  // Update timestamp
                  const latestTimestamp = finalEvents.updates[finalEvents.updates.length - 1].timestamp;
                  lastEventTimestampRef.current = latestTimestamp;
                  setLastEventTimestamp(latestTimestamp);
                  console.log('⏰ Hook: Updated final timestamp to:', latestTimestamp);
                } else {
                  console.log(`✅ Hook: Final fetch ${fetchCount} complete, no additional messages`);
                }

                // Schedule next fetch if not at max
                if (fetchCount < maxFetches) {
                  setTimeout(doFinalFetch, fetchInterval);
                } else {
                  console.log('🏁 Hook: All final fetches complete');
                  // Polling interval already cleared above, just log completion
                }
              } catch (finalError) {
                console.error(`❌ Hook: Error in final message fetch ${fetchCount}:`, finalError);
                // Polling interval already cleared above, no additional cleanup needed
              }
            };

            // Start first final fetch immediately (regular polling already stopped above)
            doFinalFetch();
          }

          // Keep polling even for UI steps to ensure modal data is fetched
          // The UniversalStepManager will handle displaying the UI when data arrives
        }
      } catch (error) {
        // Ignore abort errors - they're expected when navigating away
        if (error.name === 'AbortError') {
          console.log('🛑 Hook: Polling aborted (user navigated away)');
          return;
        }
        console.error('❌ Hook: Polling error:', error);
        // Don't stop polling on individual errors, keep trying
      }
    }, 250); // Poll every 250ms for faster UI step detection and message updates
  }, []);

  // Initialize services
  useEffect(() => {
    if (!token) return;

    // Create new AbortController for this component instance
    abortControllerRef.current = new AbortController();

    // Initialize API client with AbortController
    apiClientRef.current = new V2ApiClient(token);
    apiClientRef.current.setAbortController(abortControllerRef.current);

    // If initialRunId is provided, load existing messages and start polling
    if (initialRunId) {
      console.log('🔄 Hook: Continuing existing workflow with run_id:', initialRunId);
      setExecutionStatus('running');

      // SPINNER FIX: Set isLoading to true when loading existing workflow
      // This ensures the spinner shows during initial AI processing steps
      // The polling logic will set it to false when workflow completes
      setIsLoading(true);

      // Clear messages - no welcome message needed
      setMessages([]);

      // Load ALL existing messages from the workflow run
      (async () => {
        try {
          console.log('📥 Hook: Loading existing messages for run:', initialRunId);
          const allEvents = await apiClientRef.current.getWorkflowEvents(initialRunId, null);

          if (allEvents && allEvents.success && allEvents.updates) {
            console.log('✅ Hook: Loaded', allEvents.updates.length, 'existing events');

            // Process all existing messages immediately without delay
            processMessagesWithTiming(allEvents.updates, true);

            // Set the last event timestamp to the latest message
            if (allEvents.updates.length > 0) {
              const latestTimestamp = allEvents.updates[allEvents.updates.length - 1].timestamp;
              lastEventTimestampRef.current = latestTimestamp;
              setLastEventTimestamp(latestTimestamp);
              console.log('⏰ Hook: Set initial last event timestamp to:', latestTimestamp);
            }
          }

          // Now start polling for NEW events only
          startPollingForResults(initialRunId);
        } catch (error) {
          console.error('❌ Hook: Error loading existing messages:', error);
          // Still start polling even if loading fails
          startPollingForResults(initialRunId);
        }
      })();
    } else {
      // Clear messages for new workflow - no welcome message needed
      setMessages([]);
      setExecutionStatus('ready');
    }

    // Cleanup function - stops polling AND cancels in-flight requests
    return () => {
      console.log('🧹 Hook: Cleanup - stopping polling and aborting in-flight requests');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Abort any in-flight HTTP requests to prevent 499 errors
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [token, initialRunId, startPollingForResults]);

  // Send message function
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || !apiClientRef.current) {
      return;
    }

    // CRITICAL FIX: Only block NEW workflow starts while loading
    // Allow continuing EXISTING workflows (user responding to UI steps like modal confirmation)
    if (!runId && isLoading) {
      console.log('⚠️ Blocked duplicate workflow start - already loading');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Add user message immediately
    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      if (!runId) {
        // Start new workflow
        console.log('🚀 Hook: Starting new workflow', { workflowId, messageText });

        try {
          // Start the workflow API call
          console.log('📡 Hook: Initiating workflow start API call');
          const result = await apiClientRef.current.startWorkflow(workflowId, messageText);
          console.log('🔍 Hook: startWorkflow result:', result);

          if (result.success && result.run_id) {
            // CRITICAL: Reset all state for new workflow to prevent pollution from previous run
            console.log('🔄 Hook: Resetting state for new workflow run');
            lastEventTimestampRef.current = null;
            setLastEventTimestamp(null);
            setMessages([userMessage]); // Keep only the current user message
            setEvents([]);
            setError(null);

            // Update state
            setRunId(result.run_id);
            setExecutionStatus('running');

            console.log('✅ Hook: Workflow started for run', result.run_id);

            // Start polling for results immediately and then continue with interval
            startPollingForResults(result.run_id);

            // Check for immediate response after a short delay
            setTimeout(async () => {
              try {
                const quickCheck = await apiClientRef.current.getWorkflowEvents(result.run_id, lastEventTimestampRef.current);
                if (quickCheck && quickCheck.success && quickCheck.updates) {
                  console.log('🚀 Hook: Quick response check found', quickCheck.updates.length, 'events');
                  // Process quick response with timing
                  processMessagesWithTiming(quickCheck.updates);

                  // Update timestamp to prevent duplicates in regular polling
                  if (quickCheck.updates.length > 0) {
                    const latestTimestamp = quickCheck.updates[quickCheck.updates.length - 1].timestamp;
                    lastEventTimestampRef.current = latestTimestamp;
                    setLastEventTimestamp(latestTimestamp);
                  }
                }
              } catch (quickError) {
                console.log('⚡ Hook: Quick response check failed, will use regular polling');
              }
            }, 100); // Check after 100ms for immediate responses
          } else {
            throw new Error('Failed to start workflow - no run_id received');
          }
        } catch (error) {
          console.error('❌ Hook: Error in workflow start process:', error);
          throw error;
        }
      } else {
        // Continue existing workflow
        console.log('🔄 Hook: Continuing workflow', { runId, messageText });

        const result = await apiClientRef.current.continueWorkflow(runId, messageText);

        if (result.success) {
          setExecutionStatus('running');
          // Start polling for results after continuing workflow
          startPollingForResults(runId);

          // Check for immediate response after continuing
          setTimeout(async () => {
            try {
              const quickCheck = await apiClientRef.current.getWorkflowEvents(runId, lastEventTimestampRef.current);
              if (quickCheck && quickCheck.success && quickCheck.updates) {
                console.log('🚀 Hook: Quick continue check found', quickCheck.updates.length, 'new events');
                // Process continue response with timing
                processMessagesWithTiming(quickCheck.updates);

                // Update timestamp to prevent duplicates in regular polling
                if (quickCheck.updates.length > 0) {
                  const latestTimestamp = quickCheck.updates[quickCheck.updates.length - 1].timestamp;
                  lastEventTimestampRef.current = latestTimestamp;
                  setLastEventTimestamp(latestTimestamp);
                }
              }
            } catch (quickError) {
              console.log('⚡ Hook: Quick continue check failed, will use regular polling');
            }
          }, 100); // Check after 100ms for immediate responses
        } else {
          throw new Error('Failed to continue workflow');
        }
      }
    } catch (err) {
      console.error('❌ Hook: Failed to send message', err);

      const errorMessage = {
        id: `error_${Date.now()}`,
        type: 'error',
        content: `❌ Error: ${err.message}`,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(err.message);
      setExecutionStatus('failed');
      setIsLoading(false);
    }
  }, [workflowId, runId, isLoading]);

  // Get workflow status
  const getStatus = useCallback(async () => {
    if (!runId || !apiClientRef.current) return null;

    try {
      const status = await apiClientRef.current.getWorkflowStatus(runId);
      return status;
    } catch (err) {
      console.error('❌ Hook: Failed to get status', err);
      return null;
    }
  }, [runId]);

  // Get execution details (for debugging)
  const getExecutionDetails = useCallback(async () => {
    if (!runId || !apiClientRef.current) return null;

    try {
      const details = await apiClientRef.current.getExecutionDetails(runId);
      return details;
    } catch (err) {
      console.error('❌ Hook: Failed to get execution details', err);
      return null;
    }
  }, [runId]);

  // Reset workflow (start fresh)
  const resetWorkflow = useCallback(() => {
    // Clear polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setRunId(null);
    setMessages([{
      id: 'welcome',
      type: 'system',
      content: `🚀 Ready to start workflow execution. Send a message to begin!`,
      timestamp: new Date().toISOString()
    }]);
    setExecutionStatus('ready');
    setEvents([]);
    setError(null);
    setIsLoading(false);
    setLastEventTimestamp(null);
    setPendingMessages([]);
    lastEventTimestampRef.current = null;
  }, []);

  return {
    // State
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    events,
    error,

    // Actions
    sendMessage,
    getStatus,
    getExecutionDetails,
    resetWorkflow,

    // Computed state
    hasActiveWorkflow: !!runId,
    canSendMessage: !isLoading && !!token
  };
};